import { observer } from 'mobx-react';
import React from 'react';
import { salesLocationStore } from '../../store/salesLocationStore';
import { BRANCHES_STATUS, IBranchesReportParams } from '../../types/salesLocation';
import { allOptionsValue, defaultPagination } from 'src/constants/main';
import { USER_ROLES } from 'src/constants/user';
import { localeStore } from 'src/mobx/localesStore';
import { userStore } from 'src/mobx/userStore';
import client from 'src/services/client';
import { IBranch } from 'src/types/branches';
import { IPagination } from 'src/types/main';
import { getLastArrayItem, hasRole } from 'src/utils/helpers';
import CustomSelect from 'src/view/components/Controls/Pickers/CustomSelect/CustomSelect';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import SpinLoader from 'src/view/components/Controls/SpinLoader/SpinLoader';
import { ReportsBranchPicker } from 'src/view/screens/ReportsScreen/components/ReportsBranchPicker/ReportsBranchPicker';
import ReportsCalendar from 'src/view/screens/ReportsScreen/components/ReportsCalendar/ReportsCalendar';
import ReportsFranchiseePicker from 'src/view/screens/ReportsScreen/components/ReportsFranchiseePicker/ReportsFranchiseePicker';
import ReportContent from 'src/view/screens/ReportsScreen/containers/ReportContent/ReportContent';
import ReportHeader from 'src/view/screens/ReportsScreen/containers/ReportHeader/ReportHeader';
import { reportsStore } from 'src/view/screens/ReportsScreen/store/reportsStore';
import { appStore } from '~/mobx/appStore';
import logger from '~/services/logger';

@observer
export default class BranchSales extends React.Component<{}, {}> {
  async componentDidMount() {
    if (!salesLocationStore.reports.branches.data) await this.buildReport();
  }

  fetchBranches = async (page: number, searchValue: string = ''): Promise<IPagination<IBranch>> => {
    try {
      const params: any = {
        page,
        name: searchValue,
      };

      if (hasRole([USER_ROLES.FRANCHISEE])) params.branches = userStore.user.branches.map((branch) => branch.id);

      const pagination: IPagination<IBranch> = await client.get('/v4/branches', {
        params,
      });
      return pagination;
    } catch (error: any) {
      logger.error({ error, place: 'BranchSales fetchBranches' });
      return defaultPagination;
    }
  };

  onSelectBranches = (options: IPickerOption[]) => {
    salesLocationStore.setFilters({ branches: options });
  };

  onBranchesStatusChange = (options: IPickerOption[]) => {
    salesLocationStore.setFilters({ branchesStatus: getLastArrayItem<IPickerOption>(options) });
  };

  buildReport = async () => {
    await salesLocationStore.fetchBranchesReport();
  };

  onExportReport = async () => {
    await salesLocationStore.exportBranchesLocationReportToXls();
  };

  onCancel = () => {
    if (salesLocationStore.cancelReportBuild.branches) salesLocationStore.cancelReportBuild.branches('Cancelled');
  };

  get branchesStatusOptions(): IPickerOption[] {
    return [
      { label: localeStore.t('all_statuses'), value: BRANCHES_STATUS.All },
      { label: localeStore.t('active_branches'), value: BRANCHES_STATUS.ACTIVE },
      { label: localeStore.t('deleted_branches'), value: BRANCHES_STATUS.DELETED },
      { label: localeStore.t('disabled_branches'), value: BRANCHES_STATUS.DISABLED },
    ];
  }

  // WARNING!!! localeStore.t, used in defaultLocationReportFilters, does not work!
  get defaultSelectedOption(): IPickerOption[] {
    return [{ ...salesLocationStore.filters.branchesStatus, label: localeStore.t(salesLocationStore.filters.branchesStatus.label) }];
  }

  render() {
    return (
      <div className="d-flex flex-column full-width flex-grow-1 mt-4">
        <ReportHeader
          onGenerateReport={this.buildReport}
          onExportReport={this.onExportReport}
          exportLoader={salesLocationStore.reports.branches.loading}
        >
          <ReportsCalendar startDate={reportsStore.filters.startDate} endDate={reportsStore.filters.endDate} />
          {appStore.isAddonsAvailable.franchise ? <ReportsFranchiseePicker /> : <ReportsBranchPicker />}
          <CustomSelect
            inputClassName="report-header__picker"
            options={this.branchesStatusOptions}
            selectedOptions={this.defaultSelectedOption}
            onSelect={this.onBranchesStatusChange}
            optionsDesc="branch_status"
          />
        </ReportHeader>
        <ReportContent actualReport={salesLocationStore.reports.branches.data} reportColumnName="branchs" />
        {salesLocationStore.reports.branches.loading ? <SpinLoader onCancel={this.onCancel} /> : null}
      </div>
    );
  }
}
